/**
 * API e stato server del motore di auto-trading (SPEC-014 §7.1).
 *
 * ⚠️ Importi, quantità e confidenza sono STRINGHE decimali (ADR-0006): si
 * formattano con `lib/format.ts` e non si convertono in `number` per calcolare.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type AutoTradingMode = 'off' | 'dry_run' | 'execute';

export type AutoTradingSettings = {
  portfolioId: string;
  portfolioName: string;
  portfolioType: string;
  baseCurrency: string;
  mode: AutoTradingMode;
  maxProposalsPerRun: number;
  minOperationAmount: string | null;
  /**
   * Liquidità da tenere non investita. Vale **solo** per gli acquisti: sotto
   * soglia il motore smette di comprare e non vende per rientrare.
   * `null` = nessun vincolo, diverso da `'0'`.
   */
  minCashReserve: string | null;
  /**
   * Tetto di concentrazione per titolo (F-125). `null` = nessun tetto, che è lo
   * stato di partenza di tutti i portafogli.
   */
  concentrationCapPct: string | null;
  concentrationEnforcement: 'observe' | 'reduce' | 'block';
  /**
   * I titoli che OGGI superano il tetto. ⚠️ Non è un'azione: nessuno di questi
   * viene venduto, né ora né mai — serve a scegliere la soglia guardando il
   * proprio portafoglio invece che in astratto.
   */
  concentrationPreview: Array<{ symbol: string; name: string; pct: string }>;
  dataFreshnessMinutes: number;
  /**
   * Routine «Scelta posizioni da vendere» (F-123): all'ora impostata rilegge le
   * posizioni e propone di vendere quelle in profitto che ritiene maturate. Le
   * proposte passano dal motore come tutte le altre.
   */
  profitReviewEnabled: boolean;
  /** `HH:MM` **locale**, nel fuso di `profitReviewTimezone`. */
  profitReviewTime: string | null;
  profitReviewTimezone: string | null;
  profitReviewLastRunAt: string | null;
  /** SPEC-026 §4.3: la revisione dei profitti può proporre scambi crypto→crypto e il motore li esegue. */
  allowSwaps: boolean;
  /**
   * `false` = il portafoglio non ha dove eseguire (né simulato né collegato).
   * Il calcolo lo fa il backend: la UI non deve dedurre una regola di dominio.
   */
  executable: boolean;
};

export type SettingsResponse = {
  items: AutoTradingSettings[];
  /** Flag globale di server: senza, nessun interruttore può avere effetto. */
  featureEnabled: boolean;
};

export type Proposal = {
  id: string;
  /** Il nome leggibile (F-171), es. `Lazy-Fox-Apple39`. */
  code: string | null;
  side: string;
  status: string;
  symbol: string;
  instrumentName: string;
  /** SPEC-026 §4.3: la destinazione di uno scambio («BTC → ETH»); null per una vendita contro la cassa. */
  swapToSymbol: string | null;
  portfolioName: string;
  proposedAmount: string | null;
  approvedAmount: string | null;
  executedAmount: string | null;
  quantity: string | null;
  llmVerdict: string | null;
  llmConfidence: string | null;
  llmReason: string | null;
  llmRiskFlags: string[];
  /** Codice strutturato: la UI lo traduce ma non lo nasconde. */
  rejectionReasonCode: string | null;
  rejectionDetail: string | null;
  /** Solo per status=deferred (F-069): quando riprende, e perché è in attesa. */
  deferredUntil: string | null;
  deferReason: string | null;
  createdAt: string;
};

/** Un candidato che NON è nato, col motivo (F-137, F-138). */
export type RunDiscard = {
  id: string;
  symbol: string | null;
  portfolioName: string;
  side: string | null;
  reason: string;
  amount: string | null;
  detail: string | null;
};

export type Run = {
  id: string;
  runDate: string;
  status: string;
  /** Da cosa nasce il run: `scheduled`, `manual`, `value_trigger`, `profit_review`. */
  trigger: string;
  /** Il portafoglio della revisione dei profitti, o l'origine registrata. */
  triggerSourceDetail: string | null;
  /**
   * Il giudizio d'insieme (F-138): per la revisione dei profitti, la motivazione
   * del modello anche quando NON vende, o perché non è stato interrogato.
   */
  summary: string | null;
  candidatesCount: number;
  approvedCount: number;
  executedCount: number;
  rejectedCount: number;
  /** Proposte in attesa dell'apertura del loro mercato (F-069). */
  deferredCount: number;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
  llmTokens: number;
  proposals: Proposal[];
  discards: RunDiscard[];
};

export type UpdateSettingsInput = {
  portfolioId: string;
  mode?: AutoTradingMode;
  maxProposalsPerRun?: number;
  minOperationAmount?: string | null;
  minCashReserve?: string | null;
  concentrationCapPct?: string | null;
  concentrationEnforcement?: 'observe' | 'reduce' | 'block';
  dataFreshnessMinutes?: number;
  profitReviewEnabled?: boolean;
  profitReviewTime?: string | null;
  profitReviewTimezone?: string | null;
  allowSwaps?: boolean;
};

export const autoTradingKeys = {
  all: ['auto-trading'] as const,
  settings: ['auto-trading', 'settings'] as const,
  runs: ['auto-trading', 'runs'] as const,
};

export function useAutoTradingSettings() {
  return useQuery({
    queryKey: autoTradingKeys.settings,
    queryFn: ({ signal }) => api.get<SettingsResponse>('/auto-trading/settings', signal),
  });
}

export function useAutoTradingRuns() {
  return useQuery({
    queryKey: autoTradingKeys.runs,
    queryFn: ({ signal }) => api.get<{ items: Run[]; total: number }>('/auto-trading/runs', signal),
  });
}

export function useUpdateAutoTradingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ portfolioId, ...body }: UpdateSettingsInput) =>
      api.patch<AutoTradingSettings>(`/auto-trading/settings/${portfolioId}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: autoTradingKeys.settings }),
  });
}

export function useRunAutoTradingNow() {
  const queryClient = useQueryClient();
  return useMutation<{ runId: string; status: string }, Error, boolean | void>({
    // `force` scavalca il limite di un run al giorno. Non indebolisce la
    // garanzia di SPEC-014 §10, che riguardava il TASK che parte due volte:
    // l'indice unico è parziale sui run schedulati e continua a coprirlo.
    mutationFn: (force) =>
      api.post<{ runId: string; status: string }>('/auto-trading/run', { force: force === true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: autoTradingKeys.all });
      // Un run in `execute` scrive movimenti e ordini: portafogli e dashboard
      // mostrano dati ora superati.
      void queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Prontezza per il modal di conferma (SPEC-014.2 §A.7)
// -----------------------------------------------------------------------------

export type ReadinessBlockCode = 'feature_disabled' | 'no_portfolios' | 'all_off';

export type AutoTradingReadiness = {
  featureEnabled: boolean;
  portfolios: Array<{ id: string; name: string; mode: AutoTradingMode; executable: boolean }>;
  canRunAutoTrading: boolean;
  blockedReason: ReadinessBlockCode | null;
  /** Prosa per l'utente; il codice resta accanto — due pubblici, due bisogni. */
  blockedDetail: string | null;
  /** Nomi dei portafogli che invierebbero ordini REALI: serve al testo di pericolo. */
  executePortfolioNames: string[];
  effectiveMode: 'execute' | 'dry_run' | 'none';
};

/**
 * Il modal deve sapere cosa può offrire **prima** di aprirsi, quindi la query
 * non è legata all'apertura. Precedente diretto: `triggerFeatureEnabled` esposto
 * da `GET /api/markets` dopo il fix di F-037, nato perché la UI non poteva
 * sapere perché un interruttore acceso non faceva nulla.
 */
export function useAutoTradingReadiness() {
  return useQuery({
    queryKey: ['auto-trading', 'readiness'] as const,
    queryFn: ({ signal }) => api.get<AutoTradingReadiness>('/auto-trading/readiness', signal),
  });
}


// ---------------------------------------------------------------- Riconciliazione

export type TraceStep = {
  kind:
    | 'trigger'
    | 'daily_briefing'
    | 'briefing_item'
    | 'instrument_analysis'
    | 'wishlist_item'
    | 'proposal'
    | 'llm_check'
    | 'broker_order'
    | 'transaction'
    /** F-144: lo studio agentico che ha consegnato la proposta (SPEC-025). */
    | 'agent_run';
  label: string;
  at: string | null;
  detail: string | null;
  href: string | null;
  /** Il passo non e' avvenuto: e' informazione, non un buco da nascondere. */
  missing: boolean;
};

export type ProposalTrace = {
  proposalId: string;
  /** Il nome leggibile (F-171). */
  code: string | null;
  runId: string;
  runDate: string;
  status: string;
  side: string;
  symbol: string;
  instrumentName: string;
  instrumentId: string;
  portfolioName: string;
  portfolioId: string;
  proposedAmount: string | null;
  approvedAmount: string | null;
  executedAmount: string | null;
  quantity: string | null;
  referencePrice: string | null;
  llmVerdict: string | null;
  llmConfidence: string | null;
  llmReason: string | null;
  llmRiskFlags: string[];
  llmModel: string | null;
  llmTokens: number | null;
  /** Quante volte la verifica e' stata tentata su questa proposta (F-131). */
  llmCheckAttempts: number;
  rejectionReasonCode: string | null;
  rejectionDetail: string | null;
  deferredUntil: string | null;
  deferReason: string | null;
  createdAt: string;
  /** L'innesco all'origine della catena (F-104). `null` = non registrato. */
  trigger: { kind: string; label: string; detail: string | null; href: string | null } | null;
  /** F-144: lo studio agentico che ha consegnato la proposta, se c'e' stato. */
  agentRunId?: string | null;
  steps: TraceStep[];
};

export type ReportTraceKind = 'briefing' | 'digest' | 'analysis' | 'rotation';

export type TracedOperation = {
  proposalId: string;
  runId: string;
  runDate: string;
  side: string;
  symbol: string;
  instrumentName: string;
  portfolioName: string;
  status: string;
  amount: string | null;
  rejectionReasonCode: string | null;
  rejectionDetail: string | null;
  href: string;
};

export type ReportTrace = {
  report: {
    kind: ReportTraceKind;
    id: string;
    title: string;
    subtitle: string | null;
    status: string;
    at: string | null;
    href: string | null;
  };
  steps: TraceStep[];
  operations: TracedOperation[];
  /** `null` = la catena e' arrivata in fondo. */
  stoppedAt: { stage: string; reason: string } | null;
  activeBlocks: string[];
};

/** `null` = nessun id nel percorso: la query non parte. */
export function useProposalTrace(proposalId: string | null) {
  return useQuery({
    queryKey: ['auto-trading', 'proposal-trace', proposalId],
    queryFn: ({ signal }) => api.get<ProposalTrace>(`/auto-trading/proposals/${proposalId!}`, signal),
    enabled: proposalId !== null,
  });
}

/**
 * La stessa riconciliazione a partire da un resoconto (F-105).
 *
 * `retry: false` perche' il 404 e' un esito **normale** qui: un resoconto di un
 * altro utente risponde cosi', e ritentarlo tre volte ritarderebbe soltanto il
 * messaggio giusto.
 */
export function useReportTrace(kind: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['auto-trading', 'report-trace', kind, id],
    queryFn: ({ signal }) => api.get<ReportTrace>(`/auto-trading/reports/${kind!}/${id!}`, signal),
    enabled: Boolean(kind) && Boolean(id),
    retry: false,
  });
}
